import { IEvent } from "../interfaces/models/IEvent";

export interface VenueDetails {
  location?: string;
  venueLink?: string;
  address1?: string;
  address2?: string;
  cityName?: string;
  stateName?: string;
  zipCode?: string;
  countryName?: string;
}

export function isVirtualLocationEvent(event: IEvent | null | undefined): boolean {
  if (!event) {
    return false;
  }
  return (
    event.eventType === "virtual" ||
    event.setupType === "webinar" ||
    event.setupType === "groupWebinar"
  );
}

export function formatEventLocation(event: IEvent | null | undefined, shortDefaultLocation?: boolean): string {
  if (!event) {
    return "Location to be determined";
  }

  if (isVirtualLocationEvent(event)) {
    return "Virtual";
  }

  if (event.comingSoonLocation) {
    return event.comingSoonLocationMessage || shortDefaultLocation ? "TBD" : "Location to be determined";
  }

  const address = event.address;
  if (!address) {
    return event.location || "Location to be determined";
  }

  const cityName = address.cityName?.trim() || "";
  const stateName = address.stateName?.trim() || "";
  const countryName = address.countryName?.trim() || "";

  if (!countryName) {
    return event.location || "Location to be determined";
  }

  switch (countryName) {
    case "United States of America":
      return `${cityName}, United States of America`; 
    case "USA":
      if (stateName && stateName.length > 2) {
        return cityName ? `${cityName}, ${stateName}` : stateName;
      }
      return cityName ? `${cityName}, USA` : "USA";

    case "Canada":
    case "China":
      if (stateName && stateName.length > 2) {
        return cityName ? `${cityName}, ${stateName}` : stateName;
      }
      return cityName ? `${cityName}, ${countryName}` : countryName;

    case "United Arab Emirates":
      return cityName ? `${cityName}, UAE` : "UAE";

    case "Singapore":
      return countryName;

    default:
      return cityName ? `${cityName}, ${countryName}` : countryName;
  }
}

export function getVenueDetails(
  event: IEvent | null | undefined
): VenueDetails | null {
  if (!event) {
    return null;
  }

  if (event.comingSoonLocation) {
    return null;
  }

  const locationText = event.location?.trim() || "";

  if (locationText.length === 0) {
    return {
      location: "Coming Soon",
    };
  }

  const setupType = event.setupType;
  if (
    setupType !== "luncheon" &&
    setupType !== "dinner" &&
    setupType !== "cocktail"
  ) {
    return null;
  }

  const address = event.address;
  if (!address) {
    return null;
  }

  const venueDetails: VenueDetails = {};

  const hasRealAddressData =
    (address.cityName && address.cityName.trim().length > 0) ||
    (address.countryName && address.countryName.trim().length > 0) ||
    (address.address1 && address.address1.trim().length > 0);

  const isLocationPlaceholder =
    locationText.toLowerCase() === "coming soon" ||
    locationText.toLowerCase() === "location to be determined" ||
    locationText.toLowerCase() === "tbd" ||
    locationText.toLowerCase() === "to be determined";

  if (!(isLocationPlaceholder && hasRealAddressData)) {
    venueDetails.location = locationText;
  }

  if (
    venueDetails.location &&
    event.venueLink &&
    event.venueLink.trim().length > 0
  ) {
    venueDetails.venueLink = event.venueLink.trim();
  }

  if (address.address1 && address.address1.trim().length > 0) {
    venueDetails.address1 = address.address1.trim();
  }

  if (address.address2 && address.address2.trim().length > 0) {
    venueDetails.address2 = address.address2.trim();
  }

  if (address.cityName && address.cityName.trim().length > 0) {
    venueDetails.cityName = address.cityName.trim();
  }

  if (address.stateName && address.stateName.trim().length > 2) {
    venueDetails.stateName = address.stateName.trim();
  }

  if (address.zipCode && address.zipCode.trim().length > 2) {
    venueDetails.zipCode = address.zipCode.trim();
  }

  if (address.countryName && address.countryName.trim().length > 0) {
    venueDetails.countryName = address.countryName.trim();
  }

  if (
    !venueDetails.location &&
    !venueDetails.address1 &&
    !venueDetails.address2 &&
    !venueDetails.cityName &&
    !venueDetails.stateName &&
    !venueDetails.zipCode &&
    !venueDetails.countryName
  ) {
    return null;
  }

  return venueDetails;
}

export function formatVenueAddress(venueDetails: VenueDetails): string {
  const parts: string[] = [];

  if (venueDetails.address1) {
    parts.push(venueDetails.address1);
  }

  if (venueDetails.address2) {
    parts.push(venueDetails.address2);
  }

  const cityStateZip: string[] = [];
  if (venueDetails.cityName) {
    cityStateZip.push(venueDetails.cityName);
  }
  if (venueDetails.stateName) {
    cityStateZip.push(venueDetails.stateName);
  }
  if (venueDetails.zipCode) {
    cityStateZip.push(venueDetails.zipCode);
  }
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  if (venueDetails.countryName) {
    parts.push(venueDetails.countryName);
  }

  return parts.join(", ");
}
